/**
 * Integrytas Group — DDM (Demo Data Mirror), mobile presentation layer.
 *
 * MobileDemoToggle — the row in the More sheet's Preferences group that moves a
 * mobile session between production and the demo mirror.
 *
 * §16: mobile owns its presentation, so this is a mobile-native control rather
 * than the desktop's header icon button — it is a full-width sheet row sitting
 * beside Theme and Language, which is where a phone user already goes to change
 * how the app behaves, and it is a touch target rather than a 28px icon.
 *
 * The SWITCH itself is shared logic, not duplicated: it calls the same
 * switchDdmSchema() the desktop toggle calls, so mobile gets the session
 * re-mint and the reload — and therefore re-resolves page access, roles and
 * theme for the schema it moved to — without this layer re-implementing any of
 * it. Presentation is separate; behaviour is not.
 */

import { useState } from 'react';
import { Database } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDdmSchema, switchDdmSchema } from '../../lib/ddm_mode';

export function MobileDemoToggle() {
  const { t } = useTranslation();
  const isDemo = useDdmSchema() === 'demo';
  const [switching, setSwitching] = useState(false);

  // switchDdmSchema reloads the page, so this normally never resolves; it is
  // here so a second tap cannot land while the first is still in flight.
  const onToggle = () => {
    if (switching) return;
    setSwitching(true);
    void switchDdmSchema(isDemo ? 'public' : 'demo').finally(() => setSwitching(false));
  };

  return (
    <button
      type="button"
      className="m-sheet-item"
      style={{ justifyContent: 'space-between' }}
      onClick={onToggle}
      disabled={switching}
      aria-pressed={isDemo}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <Database /> {t('mobile.ddm.label', 'Demo data')}
      </span>
      <span className={isDemo ? 'm-demo-pill m-on' : 'm-demo-pill'}>
        {isDemo
          ? t('mobile.ddm.on', 'On')
          : t('mobile.ddm.off', 'Off')}
      </span>
    </button>
  );
}
